import { StateMachine } from "@/core/arch/state_machine";
import { selectCohort } from "./cohort";
import {
  isMissingArtifact,
  type SignalsRepository,
} from "./signals_repository";
import type { Cohort, MerchantSignalRow, SignalsAggregates } from "./types";

export type SignalsStatus =
  | "idle"
  | "loading"
  | "loaded"
  /** No build has run yet. Distinct from `error`: nothing is wrong. */
  | "empty"
  | "error";

export interface SignalsState {
  status: SignalsStatus;
  aggregates: SignalsAggregates | null;
  rows: MerchantSignalRow[];
  /** The slice currently drilled into, or null when viewing the whole base. */
  cohort: Cohort | null;
  error?: string;
}

const INITIAL: SignalsState = {
  status: "idle",
  aggregates: null,
  rows: [],
  cohort: null,
};

/**
 * Backs the Signals page.
 *
 * Both artifacts load together: the rollups drive the charts and the rows back
 * every drill-through, so a slice never needs a round trip. Filtering is
 * therefore synchronous, which is what lets any chart element be clickable
 * without each one needing its own query.
 */
export class SignalsBloc extends StateMachine<SignalsState> {
  constructor(private readonly repo: SignalsRepository) {
    super(INITIAL);
  }

  async load(): Promise<void> {
    this.setState({ ...INITIAL, status: "loading" });
    try {
      const { aggregates, rows } = await this.repo.loadBuild();
      this.setState({
        status: "loaded",
        aggregates,
        rows,
        cohort: null,
      });
    } catch (err) {
      this.setState({
        ...INITIAL,
        status: isMissingArtifact(err) ? "empty" : "error",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  drillInto(cohort: Cohort): void {
    this.setState({ ...this.state, cohort });
  }

  clearCohort(): void {
    this.setState({ ...this.state, cohort: null });
  }

  /** Rows in the active cohort, or none when nothing is drilled into. */
  get cohortRows(): MerchantSignalRow[] {
    const { cohort, rows } = this.state;
    return cohort ? selectCohort(rows, cohort.filter) : [];
  }
}
